import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert, willDestroy } from '@ember/render-modifiers';
import type { PowerSelectBeforeOptionsSignature } from 'ember-power-select/components/power-select/before-options';
import PowerSelectBeforeOptions from 'ember-power-select/components/power-select/before-options';
import type { Select } from 'ember-power-select/components/power-select-multiple';
import { emberPowerSelectIsSelected } from 'ember-power-select/helpers/ember-power-select-is-selected';
import { isGroup } from 'ember-power-select/utils/group-utils';
import { and, or } from 'ember-truth-helpers';

import type {
  Group,
  Option,
} from '../../../types/components/prism/combo-select-input.ts';
import PrismCheckbox from '../checkbox.gts';
import PrismIcon from '../icon.gts';
import PrismInput from '../input.gts';
import PrismTooltip from '../tooltip.gts';

/**
 * Returns all options starting from an array of options.
 * Includes options in nested groups. Skips collapsed groups.
 */
export function getOptionsOfOptions(
  options: (Option | Group<Option>)[],
  accumulator: Option[] = [],
): Option[] {
  return options.reduce((acc: Option[], option) => {
    // Skip collapsed groups
    if (
      isGroup(option) &&
      'unsafe_isCollapsed' in option &&
      option.unsafe_isCollapsed
    ) {
      return acc;
    }

    if (isGroup(option)) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      return getOptionsOfOptions(option.options, acc);
    }

    return [...acc, option] as Option[];
  }, accumulator);
}

interface PrismComboSelectInputMultipleSelectionBeforeOptionsSignature {
  Args: PowerSelectBeforeOptionsSignature<Option, Group<Option>>['Args'] & {
    areAllOptionsCollapsedGroups?: boolean;
    isGroupExpanded?: boolean;
    isSearchable?: boolean;
    onGroupCollapseClick?: () => void;
    onSelectAllChange?: (toggle: boolean) => unknown;
    selectAllLabel?: string;
    unsafe_collapsibleGroupBackLabel?: string;
    unsafe_selectAllWarningMessage?: string;
  };
}

export default class PrismComboSelectInputMultipleSelectionBeforeOptions extends PowerSelectBeforeOptions<
  Option,
  Group<Option>
> {
  // Override the type signature to allow for the additional args without adapting the original signature
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get additionalArgs() {
    return this
      .args as PrismComboSelectInputMultipleSelectionBeforeOptionsSignature['Args'];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get allOptionsSelected() {
    const options = this.optionsIncludingGroupedWithoutDisabled;
    return options.every((selectedOption) =>
      emberPowerSelectIsSelected([selectedOption, this.select.selected]),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasDisabledOptions() {
    return this.optionsIncludingGrouped.some(
      (option) => 'disabled' in option && option.disabled,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get optionsIncludingGrouped() {
    return getOptionsOfOptions(this.args.select.results);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get optionsIncludingGroupedWithoutDisabled() {
    return this.optionsIncludingGrouped.filter((option) => !option.disabled);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get select() {
    return this.args.select as Select<Option, Group<Option>>;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectAllWarning() {
    if (
      this.additionalArgs.unsafe_selectAllWarningMessage &&
      this.hasDisabledOptions
    ) {
      return this.additionalArgs.unsafe_selectAllWarningMessage;
    }

    return undefined;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showSearchField() {
    return (
      this.additionalArgs.isSearchable &&
      !this.additionalArgs.areAllOptionsCollapsedGroups
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showSelectAllOption() {
    if (this.optionsIncludingGroupedWithoutDisabled.length === 0) {
      return false;
    }

    return (
      this.additionalArgs.selectAllLabel &&
      !this.additionalArgs.areAllOptionsCollapsedGroups
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get someOptionsSelected() {
    const options = this.optionsIncludingGroupedWithoutDisabled;
    return options.some((selectedOption) =>
      emberPowerSelectIsSelected([selectedOption, this.select.selected]),
    );
  }

  @action
  handleBackLabelClick(): void {
    this.additionalArgs.onGroupCollapseClick?.();
  }

  @action
  handleMouseEnter(): void {
    // Makes sure no other options are highlighted by power-select's internal API
    this.args.select.actions.highlight(null);
  }

  @action
  handleSelectAllOptionsClick(): void {
    const shouldAllOptionsBeSelected = !this.allOptionsSelected;

    // Take into account the already selected options and add/remove the current "all options"
    // Make sure group options only get added once if some are already selected
    const options = new Set(this.select.selected);
    if (shouldAllOptionsBeSelected) {
      this.optionsIncludingGrouped.forEach((option) => options.add(option));
    } else {
      this.optionsIncludingGrouped.forEach((option) => options.delete(option));
    }

    const optionsWithoutDisabled = Array.from(options).filter(
      (option) => !option.disabled,
    );
    this.select.actions.select(optionsWithoutDisabled);

    if (this.additionalArgs.onSelectAllChange) {
      this.additionalArgs.onSelectAllChange(shouldAllOptionsBeSelected);
    }
  }

  <template>
    {{! template-lint-disable no-nested-interactive require-context-role require-mandatory-role-attributes require-presentational-children }}
    {{#if
      (or
        (and
          this.additionalArgs.isGroupExpanded
          this.additionalArgs.unsafe_collapsibleGroupBackLabel
        )
        this.showSearchField
        this.showSelectAllOption
      )
    }}
      <div class="prism-combo-select-input__multiple-selection-before-options">
        {{#if
          (and
            this.additionalArgs.isGroupExpanded
            this.additionalArgs.unsafe_collapsibleGroupBackLabel
          )
        }}
          <li
            class="multiple-selection-before-options__back-label"
            data-test-id="before-options-back-label"
            role="option"
            {{on "click" this.handleBackLabelClick}}
            {{on "mouseenter" this.handleMouseEnter}}
          >
            <PrismIcon @name="chevron-left" @size="small" />
            <p class="text-300--regular truncate">
              {{this.additionalArgs.unsafe_collapsibleGroupBackLabel}}
            </p>
          </li>
        {{/if}}

        {{#if this.showSearchField}}
          <div
            class="prism-combo-select-input__search-container"
            data-test-id="before-options-search-field"
          >
            <PrismInput
              @autocomplete="off"
              @iconAfter={{hash name="search"}}
              @onInput={{@onInput}}
              @onKeyDown={{@onKeydown}}
              @removePadding={{true}}
              @value={{@select.searchText}}
              {{didInsert this.focusInput}}
              {{willDestroy this.clearSearch}}
            />
          </div>
        {{/if}}

        {{#if this.showSelectAllOption}}
          <div
            aria-selected={{if this.allOptionsSelected "true"}}
            class="multiple-selection-before-options__select-all"
            data-test-id="before-options-select-all"
            role="option"
            {{on "click" this.handleSelectAllOptionsClick}}
            {{on "mouseenter" this.handleMouseEnter}}
          >
            <div class="multiple-selection-before-options__checkbox">
              <PrismCheckbox
                @checked={{this.allOptionsSelected}}
                @indeterminate={{this.someOptionsSelected}}
                tabindex="-1"
              >
                {{this.additionalArgs.selectAllLabel}}
              </PrismCheckbox>

              {{#if this.selectAllWarning}}
                <PrismIcon
                  @name="warning"
                  data-test-id="before-options-select-all-warning"
                />
              {{/if}}
            </div>

            {{#if this.selectAllWarning}}
              <PrismTooltip @side="right" @text={{this.selectAllWarning}} />
            {{/if}}
          </div>
        {{/if}}
      </div>
    {{/if}}
  </template>
}
